import {
  moduleMetadataIntegration,
  init as sentryInit,
  setUser as sentrySetUser,
  ErrorEvent,
  makeMultiplexedTransport,
  makeFetchTransport,
  browserSessionIntegration,
  browserApiErrorsIntegration,
} from '@sentry/browser'
import { Plugins, Container } from '@getgo/container-client'
import { version } from '../version'
import { Integration } from '@sentry/core'

export const EXTRA_KEY = 'ROUTE_TO'

const beforeSend = (event: ErrorEvent) => {
  const frames = event?.exception?.values?.[0].stacktrace?.frames
  if (frames) {
    // Find the last frame with module metadata containing a DSN
    const routeTo = frames
      .filter(frame => frame.module_metadata && frame.module_metadata.dsn)
      .map(v => v.module_metadata)
      .slice(-1)

    if (routeTo.length) {
      event.extra = {
        ...event.extra,
        [EXTRA_KEY]: routeTo,
      }
    }
  }

  return event
}

const transport = makeMultiplexedTransport(makeFetchTransport, args => {
  const event = args.getEvent()
  if (event && event.extra && EXTRA_KEY in event.extra && Array.isArray(event.extra[EXTRA_KEY])) {
    return event.extra[EXTRA_KEY]
  }
  return []
})

const integrations = (defaultIntegrations: Integration[]) => {
  // Filter out some default integrations that we don't want to use.
  const integrationsToDisable = [
    // BrowserSession integration - it sends too many POST requests. It considers each SPA navigation like a web app
    // reload and therefore a new user "session", so it sends 3-4 requests per SPA navigation. These requests are a bit
    // too much and some users see some slow down due to them.
    browserSessionIntegration().name,

    // BrowserApiErrors integration - it instruments `addEventListener` and other basic browser APIs, which leads to
    // some events like `beforeunload` not being handled correctly for the softphone window.
    browserApiErrorsIntegration().name,
  ]
  const result = defaultIntegrations.filter(integration => {
    const shouldBeDisabled = integrationsToDisable.includes(integration.name)
    return !shouldBeDisabled
  })

  // When the ModuleMetadata integration is added, the metadata passed to the bundler plugin will be added to the stack
  // frames of all events under the module_metadata property. This can be used to help tag or route events from
  // different teams or sources.
  // See https://docs.sentry.io/platforms/javascript/configuration/integrations/modulemetadata/
  result.push(moduleMetadataIntegration())

  return result
}

export const initSentry = (environment: string) => {
  sentryInit({
    dsn: 'https://a61aba7e4915d0f1da8d95de053b6e06@o173976.ingest.us.sentry.io/4505987624599552',
    release: version,
    environment,
    beforeSend,
    transport,
    integrations,
  })
  if (Container.isPluginAvailable('ExecutionContext')) {
    const { machineId } = Plugins.ExecutionContext.getContext()
    sentrySetUser({
      id: machineId,
    })
  }
}
